import { WorkforceSchedule } from '../../types'
import { apiSlice } from '../api/apiSlice'
import SettlementResponse from '../../types/response/SettlementResponse'
import PlannedSettlement from '../../types/models/PlannedSettlement'
import dayjs from 'dayjs'

export const extendedApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getWorkforceSchedules: builder.query<WorkforceSchedule[], Args>({
      query: ({ id, props }) => ({
        url: `workforce-schedule/${id}`,
        params: props,
      }),
      transformResponse: (responseData: WorkforceSchedule[]) => {
        return responseData
      },
      providesTags: (_result, _error, { props }) => {
        const month = props.date ? dayjs(props.date).format('YYYY-MM') : 'ALL'
        return [{ type: 'WorkforceSchedule', id: month }]
      },
    }),
    createWorkforceSchedule: builder.mutation<void, CreateWorkforceRequest>({
      query: ({ workforceSchedules, leaderId }) => ({
        url: `/workforce-schedule/${leaderId}`,
        method: 'POST',
        body: workforceSchedules,
      }),
      invalidatesTags: (_result, _error, { workforceSchedules }) => {
        const months = new Set(workforceSchedules.map(schedule => dayjs(schedule.date).format('YYYY-MM')))
        return Array.from(months).map(month => ({
          type: 'WorkforceSchedule',
          id: month,
        }))
      },
    }),
    getRemainingWorkforceSchedules: builder.query<Map<string, number>, RemainingArgs>({
      query: ({ id, props }) => ({
        url: `workforce-schedule/${id}/remaining`,
        params: props,
      }),
      providesTags: (_result, _error, { props }) => {
        const employeeTags =
          props.employeesList?.map(employeeId => ({
            type: 'User' as const,
            id: employeeId,
          })) || []

        return [{ type: 'WorkforceSchedule', id: 'LIST' }, ...employeeTags]
      },
    }),
    getCompletedSettlements: builder.query<SettlementResponse[], Args>({
      query: ({ id, props }) => ({
        url: `/workforce-schedule/${id}/completed-settlement`,
        params: props,
      }),
      providesTags: () => [
        { type: 'WorkforceSchedule', id: 'LIST' },
        { type: 'Worklog', id: 'LIST' },
        { type: 'User', id: 'LIST' },
      ],
    }),
    getPlannedSettlements: builder.query<PlannedSettlement[], Args>({
      query: ({ id, props }) => ({
        url: `/workforce-schedule/${id}/planned-settlement`,
        params: props,
      }),
      providesTags: () => [
        { type: 'WorkforceSchedule', id: 'LIST' },
        { type: 'Worklog', id: 'LIST' },
        { type: 'User', id: 'LIST' },
      ],
    }),
  }),
})

export type WorkforceScheduleModify = {
  id?: string
  projectId: string
  employeeId: string
  date: string
  percent: number
  description?: string
  removed: boolean
}

type CreateWorkforceRequest = {
  workforceSchedules: WorkforceScheduleModify[]
  leaderId: string
}

export type WorkforceRequestProps = {
  fromDate?: string
  toDate?: string
  date?: string
  sortField?: string
}

type Args = {
  id: string
  props: WorkforceRequestProps
}

export type WorkforceRemainingRequestProps = {
  date: string
  employeesList?: string[]
  excludedProjects?: string[]
}

type RemainingArgs = {
  id: string
  props: WorkforceRemainingRequestProps
}

// Generated hooks
export const {
  useGetWorkforceSchedulesQuery,
  useCreateWorkforceScheduleMutation,
  useLazyGetWorkforceSchedulesQuery,
  useGetCompletedSettlementsQuery,
  useGetPlannedSettlementsQuery,
  useGetRemainingWorkforceSchedulesQuery,
} = extendedApiSlice